// src/components/Onboarding/canvas/overlayDrawing.js
// Overlay drawing functions for canvas

import { SHIMMER_CONFIG } from './canvasConstants';
import { cropImageToMask, createCoordinateMappers } from './canvasUtils';
import { drawAnimation, drawBodySweepSequence } from './animationDrawing';
import { drawSkeletonShimmer } from './shimmerDrawing';

/**
 * Draw overlay on canvas
 * @param {CanvasRenderingContext2D} ctx - Main canvas context
 * @param {Object} overlay - Overlay definition
 * @param {Object} srcRect - Source rect in image space (crop region)
 * @param {Object} fit - Destination rect in canvas space
 * @param {number} imageWidth - Full image width
 * @param {number} imageHeight - Full image height
 * @param {Map} overlayImages - Legacy loaded overlay images (data URL -> Image)
 * @param {Object} maskRefs - New mask canvas refs for compositing
 */
export function drawOverlay(ctx, overlay, srcRect, fit, imageWidth, imageHeight, overlayImages, maskRefs = {}) {
  const { type, coords, style = {}, label, labelPosition = 'top', labelSize } = overlay;
  const { bodyMaskCanvas, hairMaskCanvas, featheredBodyMaskCanvas, featheredHairMaskCanvas, fxCanvasRef } = maskRefs;

  const { mapX, mapY, mapSize, isVisible } = createCoordinateMappers(srcRect, fit);

  ctx.save();

  // Apply styles
  if (style.strokeStyle) ctx.strokeStyle = style.strokeStyle;
  if (style.fillStyle) ctx.fillStyle = style.fillStyle;
  if (style.lineWidth) ctx.lineWidth = style.lineWidth;
  if (style.globalAlpha !== undefined) ctx.globalAlpha = style.globalAlpha;
  if (style.lineDash) ctx.setLineDash(style.lineDash);

  try {
    switch (type) {
      case 'rect': {
        if (!isVisible(coords.x, coords.y, coords.width, coords.height)) {
          break;
        }
        
        const x = mapX(coords.x);
        const y = mapY(coords.y);
        const w = mapSize(coords.width, 'width');
        const h = mapSize(coords.height, 'height');
        
        if (style.fillStyle) ctx.fillRect(x, y, w, h);
        if (style.strokeStyle) ctx.strokeRect(x, y, w, h);
        break;
      }

      case 'circle': {
        if (!isVisible(coords.x, coords.y)) {
          break;
        }
        
        const cx = mapX(coords.x);
        const cy = mapY(coords.y);
        const r = mapSize(coords.radius, 'width');
        
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        if (style.fillStyle) ctx.fill();
        if (style.strokeStyle) ctx.stroke();
        break;
      }

      case 'line': {
        if (!isVisible(coords.start.x, coords.start.y) && !isVisible(coords.end.x, coords.end.y)) {
          break;
        }
        
        const startX = mapX(coords.start.x);
        const startY = mapY(coords.start.y);
        const endX = mapX(coords.end.x);
        const endY = mapY(coords.end.y);
        
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        if (style.strokeStyle) ctx.stroke();
        break;
      }

      case 'polygon': {
        if (coords.points && coords.points.length > 0) {
          ctx.beginPath();
          const first = coords.points[0];
          ctx.moveTo(mapX(first.x), mapY(first.y));
          coords.points.slice(1).forEach(pt => {
            ctx.lineTo(mapX(pt.x), mapY(pt.y));
          });
          ctx.closePath();
          if (style.fillStyle) ctx.fill();
          if (style.strokeStyle) ctx.stroke();
        }
        break;
      }

      case 'image': {
        if (overlay.imageUrl && overlayImages) {
          const img = overlayImages.get(overlay.imageUrl);
          if (img && img.complete) {
            const overlayScaleX = img.width / imageWidth;
            const overlayScaleY = img.height / imageHeight;
            
            const srcOverlayX = srcRect.x * overlayScaleX;
            const srcOverlayY = srcRect.y * overlayScaleY;
            const srcOverlayW = srcRect.width * overlayScaleX;
            const srcOverlayH = srcRect.height * overlayScaleY;
            
            ctx.drawImage(
              img,
              srcOverlayX, srcOverlayY, srcOverlayW, srcOverlayH,
              fit.x, fit.y, fit.width, fit.height
            );
          }
        }
        break;
      }

      case 'coloredArea': {
        ctx.save();
        
        if (overlay.mode === 'full') {
          ctx.fillStyle = overlay.color;
          ctx.fillRect(fit.x, fit.y, fit.width, fit.height);
        } else if (overlay.mode === 'non-body' || overlay.mode === 'hair') {
          const useNewPath = (overlay.mode === 'non-body' && bodyMaskCanvas) || 
                            (overlay.mode === 'hair' && hairMaskCanvas);
          
          if (useNewPath) {
            const maskCanvas = overlay.mode === 'non-body' ? bodyMaskCanvas : hairMaskCanvas;
            
            let fxCanvas = fxCanvasRef?.current;
            if (!fxCanvas) {
              fxCanvas = document.createElement('canvas');
              if (fxCanvasRef) fxCanvasRef.current = fxCanvas;
            }
            
            const fxW = Math.ceil(fit.width);
            const fxH = Math.ceil(fit.height);
            if (fxCanvas.width !== fxW || fxCanvas.height !== fxH) {
              fxCanvas.width = fxW;
              fxCanvas.height = fxH;
            }
            const fxCtx = fxCanvas.getContext('2d');
            
            fxCtx.clearRect(0, 0, fxW, fxH);
            fxCtx.fillStyle = overlay.color;
            fxCtx.fillRect(0, 0, fxW, fxH);
            
            const maskCrop = cropImageToMask(
              srcRect,
              imageWidth,
              imageHeight,
              maskCanvas.width,
              maskCanvas.height
            );
            
            if (overlay.mode === 'non-body') {
              fxCtx.globalCompositeOperation = 'destination-out';
              fxCtx.drawImage(
                maskCanvas,
                maskCrop.x, maskCrop.y, maskCrop.width, maskCrop.height,
                0, 0, fxW, fxH
              );
            } else if (overlay.mode === 'hair') {
              fxCtx.globalCompositeOperation = 'destination-in';
              fxCtx.drawImage(
                maskCanvas,
                maskCrop.x, maskCrop.y, maskCrop.width, maskCrop.height,
                0, 0, fxW, fxH
              );
            }
            
            fxCtx.globalCompositeOperation = 'source-over';
            ctx.drawImage(fxCanvas, fit.x, fit.y);
            
          } else if (overlay.maskUrl && overlayImages) {
            const maskImg = overlayImages.get(overlay.maskUrl);
            
            if (!maskImg || !maskImg.complete) {
              break;
            }
            
            const maskScaleX = maskImg.width / imageWidth;
            const maskScaleY = maskImg.height / imageHeight;
            const srcMaskX = srcRect.x * maskScaleX;
            const srcMaskY = srcRect.y * maskScaleY;
            const srcMaskW = srcRect.width * maskScaleX;
            const srcMaskH = srcRect.height * maskScaleY;
            
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = Math.ceil(fit.width);
            tempCanvas.height = Math.ceil(fit.height);
            const tempCtx = tempCanvas.getContext('2d');
            
            tempCtx.fillStyle = overlay.color;
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            
            if (overlay.mode === 'non-body') {
              tempCtx.globalCompositeOperation = 'destination-out';
            } else if (overlay.mode === 'hair') {
              tempCtx.globalCompositeOperation = 'destination-in';
            }
            
            tempCtx.drawImage(
              maskImg,
              srcMaskX, srcMaskY, srcMaskW, srcMaskH,
              0, 0, tempCanvas.width, tempCanvas.height
            );
            
            tempCtx.globalCompositeOperation = 'source-over';
            ctx.drawImage(tempCanvas, fit.x, fit.y);
          } else {
            ctx.fillStyle = overlay.color;
            ctx.fillRect(fit.x, fit.y, fit.width, fit.height);
          }
        }
        
        ctx.restore();
        break;
      }

      case 'shimmer': {
        ctx.save();
        
        const periodMs = overlay.periodMs ?? SHIMMER_CONFIG.periodMs;
        const angleDeg = overlay.angleDeg ?? SHIMMER_CONFIG.angleDeg;
        const baseColor = overlay.baseColor ?? SHIMMER_CONFIG.baseColor;
        const shimmerColor = overlay.shimmerColor ?? SHIMMER_CONFIG.shimmerColor;
        const bandWidth = overlay.bandWidth ?? SHIMMER_CONFIG.bandWidth;
        const gradientStops = overlay.gradientStops ?? SHIMMER_CONFIG.gradientStops;
        
        const now = performance.now();
        const phase = (now % periodMs) / periodMs;
        
        const shimmerParams = {
          phase,
          angleDeg,
          baseColor,
          shimmerColor,
          bandWidth,
          gradientStops,
        };
        
        const maskCanvas = overlay.mode === 'non-body' ? bodyMaskCanvas :
                          overlay.mode === 'hair' ? hairMaskCanvas : null;
        
        if (!maskCanvas || overlay.mode === 'full') {
          drawSkeletonShimmer(ctx, fit.x, fit.y, fit.width, fit.height, shimmerParams);
        } else {
          let fxCanvas = fxCanvasRef?.current;
          if (!fxCanvas) {
            fxCanvas = document.createElement('canvas');
            if (fxCanvasRef) fxCanvasRef.current = fxCanvas;
          }
          
          const fxW = Math.ceil(fit.width);
          const fxH = Math.ceil(fit.height);
          if (fxCanvas.width !== fxW || fxCanvas.height !== fxH) {
            fxCanvas.width = fxW;
            fxCanvas.height = fxH;
          }
          const fxCtx = fxCanvas.getContext('2d');
          
          fxCtx.clearRect(0, 0, fxW, fxH);
          drawSkeletonShimmer(fxCtx, 0, 0, fxW, fxH, shimmerParams);
          
          const maskCrop = cropImageToMask(
            srcRect, imageWidth, imageHeight,
            maskCanvas.width, maskCanvas.height
          );
          
          if (overlay.mode === 'non-body') {
            fxCtx.globalCompositeOperation = 'destination-out';
          } else {
            fxCtx.globalCompositeOperation = 'destination-in';
          }
          
          const featheredMask = overlay.mode === 'non-body' 
            ? (featheredBodyMaskCanvas || maskCanvas)
            : (featheredHairMaskCanvas || maskCanvas);
          
          const actualMaskCrop = cropImageToMask(
            srcRect, imageWidth, imageHeight,
            featheredMask.width, featheredMask.height
          );
          
          fxCtx.drawImage(
            featheredMask,
            actualMaskCrop.x, actualMaskCrop.y, actualMaskCrop.width, actualMaskCrop.height,
            0, 0, fxW, fxH
          );
          
          fxCtx.globalCompositeOperation = 'source-over';
          ctx.drawImage(fxCanvas, fit.x, fit.y);
        }
        
        ctx.restore();
        break;
      }

      case 'animation': {
        if (overlay.maskUrl && overlayImages) {
          const maskImg = overlayImages.get(overlay.maskUrl);
          
          if (maskImg && maskImg.complete) {
            const maskScaleX = maskImg.width / imageWidth;
            const maskScaleY = maskImg.height / imageHeight;
            
            const srcMaskX = srcRect.x * maskScaleX;
            const srcMaskY = srcRect.y * maskScaleY;
            const srcMaskW = srcRect.width * maskScaleX;
            const srcMaskH = srcRect.height * maskScaleY;
            
            ctx.save();
            
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = fit.width;
            tempCanvas.height = fit.height;
            const tempCtx = tempCanvas.getContext('2d');
            
            tempCtx.drawImage(
              maskImg,
              srcMaskX, srcMaskY, srcMaskW, srcMaskH,
              0, 0, fit.width, fit.height
            );
            
            tempCtx.globalCompositeOperation = 'source-in';
            
            if (overlay.animation === 'bodySweepSequence' && overlay.sweepConfig) {
              drawBodySweepSequence(ctx, tempCtx, maskImg, overlay.sweepConfig, fit, srcMaskX, srcMaskY, srcMaskW, srcMaskH);
            } else {
              drawAnimation(tempCtx, overlay.animation, fit.width, fit.height, overlay.subtype);
              ctx.drawImage(tempCanvas, fit.x, fit.y);
            }
            
            ctx.restore();
          }
        }
        break;
      }

      default:
        console.warn('[InteractiveCanvas] Unknown overlay type:', type);
    }
    
    // Draw label if present
    if (label) {
      if (type === 'rect' && !isVisible(coords.x, coords.y, coords.width, coords.height)) {
        ctx.restore();
        return;
      }
      if (type === 'circle' && !isVisible(coords.x, coords.y)) {
        ctx.restore();
        return;
      }
      if (type === 'line' && !isVisible(coords.start.x, coords.start.y) && !isVisible(coords.end.x, coords.end.y)) {
        ctx.restore();
        return;
      }
      
      ctx.restore();
      ctx.save();
      
      let labelX, labelY;
      if (type === 'rect') {
        const x = mapX(coords.x);
        const y = mapY(coords.y);
        const h = mapSize(coords.height, 'height');
        labelX = x + 5;
        labelY = labelPosition === 'top' ? y - 5 : y + h + 15;
      } else if (type === 'circle') {
        const cx = mapX(coords.x);
        const cy = mapY(coords.y);
        const r = mapSize(coords.radius, 'width');
        labelX = labelPosition === 'right' ? cx + r + 8 : cx - r - 8;
        labelY = cy + 4;
      } else if (type === 'line') {
        const midX = (coords.start.x + coords.end.x) / 2;
        const midY = (coords.start.y + coords.end.y) / 2;
        labelX = mapX(midX) + 5;
        labelY = mapY(midY) - 5;
      } else {
        labelX = 10;
        labelY = 20;
      }
      
      const fontSize = labelSize === 'small' ? '10px' : '12px';
      const fontHeight = labelSize === 'small' ? 16 : 20;
      ctx.font = `${fontSize} system-ui, -apple-system, sans-serif`;
      ctx.textBaseline = 'middle';
      const metrics = ctx.measureText(label);
      const padding = labelSize === 'small' ? 3 : 4;
      const labelWidth = metrics.width + padding * 2;
      const labelHeight = fontHeight;
      
      labelX = Math.max(fit.x + padding, Math.min(labelX, fit.x + fit.width - labelWidth - padding));
      labelY = Math.max(fit.y + 10, Math.min(labelY, fit.y + fit.height - 10));
      
      ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
      ctx.fillRect(
        labelX - padding,
        labelY - fontHeight / 2,
        labelWidth,
        labelHeight
      );
      
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, labelX, labelY);
    }
  } catch (e) {
    console.error('[InteractiveCanvas] Error drawing overlay:', e);
  }

  ctx.restore();
}
